<template>
	<div class="balance-management">
		<!-- Issuing Introduction Banner -->
		<CardSlashed class="card-link card-link-primary" v-if="showIntroductionBanner">
			<template #left>
				<div class="card-link-title">
					<h2>{{ getString('issuing_balance_intro_title') }}</h2>
					<p>{{ getString('issuing_balance_intro_explain') }}</p>
					<div class="intro-steps mt-20">
						<div class="step-item">
							<span class="step-number">1</span>
							<div class="step-content">
								<h3>{{ getString('issuing_balance_step1_title') }}</h3>
								<p>{{ getString('issuing_balance_step1_desc') }}</p>
							</div>
						</div>
						<div class="step-item">
							<span class="step-number">2</span>
							<div class="step-content">
								<h3>{{ getString('issuing_balance_step2_title') }}</h3>
								<p>{{ getString('issuing_balance_step2_desc') }}</p>
							</div>
						</div>
						<div class="step-item">
							<span class="step-number">3</span>
							<div class="step-content">
								<h3>{{ getString('issuing_balance_step3_title') }}</h3>
								<p>{{ getString('issuing_balance_step3_desc') }}</p>
							</div>
						</div>
					</div>
				</div>
			</template>
			<template #right>
				<div class="wrapper-img">
					<picture>
						<source type="image/webp" srcset="https://img.qromo.io/img/banner-issuing.webp">
						<img src="https://img.qromo.io/img/banner-issuing.png" alt="Qromo Cards">
					</picture>
				</div>
			</template>
		</CardSlashed>

		<div class="balance-info">
			<div class="balance-selector-container">
				<BalanceDisplay />
			</div>
			<div class="balance-chart-container">
				<BalanceChart />
			</div>
		</div>
		<div class="balance-transactions-tables mt-30">
			<BalanceTransactionsTable />
		</div>
	</div>
</template>

<script>
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import BalanceDisplay from "./BalanceDisplay.vue";
import BalanceChart from "./BalanceChart.vue";
import BalanceTransactionsTable from "./BalanceTransactionsTable.vue";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";

export default {
	setup() {
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		return {
			cardsStore,
			languageStore,
			userStore,
			getString: languageStore.getString
		}
	},
	name: "CardsBalanceContainer",
	components: {BalanceDisplay, BalanceChart, BalanceTransactionsTable, CardSlashed},
	computed: {
		showIntroductionBanner() {
			// Show banner if:
			// 1. Balance is 0
			// 2. No cards have been issued yet
			// 3. No transactions in the transaction data
			// 4. Is not loading data

			if (this.cardsStore.loadingBalance) return false;

			const hasNoBalance = this.cardsStore.issuingBalance === 0;
			const hasNoCards = !this.cardsStore.businessCards || this.cardsStore.businessCards.length === 0;
			
			// Check if there are any actual transactions across all cached data
			let hasNoTransactions = true;
			const transactionData = this.cardsStore.cardTransactions.data;
			for (const cacheKey in transactionData) {
				const data = transactionData[cacheKey];
				if (data && ((data.transactions && data.transactions.length > 0) || 
					(data.authorizations && data.authorizations.length > 0))) {
					hasNoTransactions = false;
					break;
				}
			}

			return hasNoBalance && hasNoCards && hasNoTransactions;
		}
	},
	mounted() {
		// TODO: Implement balance data fetching
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== oldVal) {
				// TODO: Refresh balance data when business changes
			}
		}
	}
}
</script>

<style scoped lang="scss">
.balance-management {
	display: flex;
	flex-direction: column;
	margin-top: var(--main-padding);
}

// Introduction banner styles
.card-link {
	margin-bottom: var(--main-padding);
	
	// Remove padding from card body
	&:deep(.card-body) {
		padding: 0 !important;
	}
	
	.card-link-title {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		color: var(--color-txt-white);
		max-width: 600px;
		padding: 15px;
		text-align: left;

		h2 {
			width: 100%;
			font-size: 24px;
			text-align: left;
			color: var(--color-txt-white);
			margin: 0;
		}
		
		p {
			margin-top: 10px;
			font-size: 14px;
			line-height: 20px;
			color: var(--color-txt-white);
			opacity: 0.9;
			text-align: left;
		}
		
		.intro-steps {
			display: flex;
			flex-direction: column;
			gap: 15px;
			width: 100%;
			
			.step-item {
				display: flex;
				align-items: flex-start;
				gap: 15px;
				
				.step-number {
					display: flex;
					align-items: center;
					justify-content: center;
					min-width: 28px;
					height: 28px;
					border-radius: 50%;
					background-color: rgba(255, 255, 255, 0.2);
					color: var(--color-txt-white);
					font-size: 14px;
					font-weight: 600;
				}
				
				.step-content {
					flex: 1;
					
					h3 {
						font-size: 16px;
						font-weight: 600;
						color: var(--color-txt-white);
						margin: 0 0 5px 0;
					}
					
					p {
						font-size: 13px;
						line-height: 18px;
						color: var(--color-txt-white);
						opacity: 0.85;
						margin: 0;
					}
				}
			}
		}
		
		@media (max-width: 500px) {
			h2 {font-size: 20px;}
			p {font-size: 12px;}
			
			.intro-steps {
				.step-item {
					.step-number {
						min-width: 24px;
						height: 24px;
						font-size: 12px;
					}
					
					.step-content {
						h3 {
							font-size: 14px;
						}
						
						p {
							font-size: 12px;
							line-height: 16px;
						}
					}
				}
			}
		}
	}
	
	.wrapper-img {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		picture {
			img {
				width: 100%;
				height: 100%;
				max-height: 220px;
				object-fit: contain;
				@media (max-width: 550px) {
					max-height: 190px;
				}
			}
		}
	}
	
	&-primary {
		&:deep(.slashed-card-container) {
			background: linear-gradient(198deg, #0D8F82 0%, rgb(47, 36, 209) 100%);
			.slashed-left {
				width: 80%;
				justify-content: flex-start;
				.card-link-title {
					width: 100%;
					max-width: unset;
				}

				@media (max-width: 550px) {
					width: 100%;
					padding-right: 10px;
					padding-bottom: 10px;
					padding-top: 0;
				}
			}
			.slashed-right {
				display: flex;
				align-items: center;
				justify-content: center;
				@media (max-width: 550px) {
					padding-top: 10px;
					padding-bottom: 0;
				}
			}

			@media (max-width: 550px) {
				flex-direction: column-reverse;
			}
		}
	}
}

.balance-info {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: stretch;
	gap: 20px;

	@media (max-width: 1024px) {
		flex-direction: column;
	}

	.balance-selector-container {
		width: 50%;

		@media (max-width: 1024px) {
			width: 100%;
		}
	}

	.balance-chart-container {
		width: calc(50% - 20px);

		@media (max-width: 1024px) {
			width: 100%;
		}
	}
}
</style>